import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

interface AnchoredMenuProps {
  anchorRef: React.RefObject<HTMLElement | null>
  open: boolean
  children: React.ReactNode
  className?: string
}

/** Renders a menu on document.body so parent overflow does not clip it. */
export function AnchoredMenu({ anchorRef, open, children, className }: AnchoredMenuProps) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null)

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null)
      return
    }

    const update = () => {
      const node = anchorRef.current
      if (!node) return
      const rect = node.getBoundingClientRect()
      const menuHeight = 240
      const spaceBelow = window.innerHeight - rect.bottom
      const openUpward = spaceBelow < menuHeight && rect.top > spaceBelow
      setStyle({
        position: 'fixed',
        left: Math.max(8, rect.left),
        width: Math.max(rect.width, 180),
        top: openUpward ? Math.max(8, rect.top - menuHeight - 4) : rect.bottom + 4,
        maxHeight: openUpward
          ? Math.max(120, rect.top - 12)
          : Math.max(120, Math.min(menuHeight, spaceBelow - 12)),
        zIndex: 80,
      })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [anchorRef, open])

  if (!open || !style) return null

  return createPortal(
    <div
      style={style}
      className={className || 'overflow-y-auto rounded-md border border-border bg-background shadow-lg'}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  )
}
